import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { Tooltip } from '../common/Tooltip';
import { WorkflowExecutionCard } from './WorkflowExecutionCard';
import { WorkflowExecution } from '../../types/workflow';
import {
  FiPlay, FiPause, FiCheck, FiX, FiClock, FiUsers, FiZap,
  FiTrendingUp, FiActivity, FiLayers, FiGitBranch, FiTarget,
  FiBarChart, FiCpu, FiDatabase, FiSettings, FiInfo, FiEdit,
  FiChevronUp, FiChevronDown, FiStar, FiTrendingDown,
  FiAlertTriangle, FiAward, FiCalendar, FiHash, FiTool, FiTrash2
} from 'react-icons/fi';
import './WorkflowCard.css';

// Executions table component with sorting
const ExecutionsTable: React.FC<{
  executions: WorkflowExecution[];
  metrics: any;
  darkMode: boolean;
}> = ({ executions, metrics, darkMode }) => {
  const [sortField, setSortField] = useState<'status' | 'created_at' | 'duration' | 'progress'>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [hoveredExecution, setHoveredExecution] = useState<WorkflowExecution | null>(null);
  const [hoverPosition, setHoverPosition] = useState({ x: 0, y: 0 });

  const formatDuration = (durationMs: number) => {
    const days = Math.floor(durationMs / (1000 * 60 * 60 * 24));
    const hours = Math.floor((durationMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0 || parts.length === 0) parts.push(`${minutes}m`);

    return parts.join(' ');
  };

  const handleSort = (field: 'status' | 'created_at' | 'duration' | 'progress') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const handleRowHover = (execution: WorkflowExecution, event: React.MouseEvent) => {
    const cardWidth = 320;
    const cardHeight = 200;

    let x = event.clientX + 15;
    let y = event.clientY - (cardHeight / 2);

    if (x + cardWidth > window.innerWidth) {
      x = event.clientX - cardWidth - 15;
    }

    if (y < 10) {
      y = 10;
    } else if (y + cardHeight > window.innerHeight - 10) {
      y = window.innerHeight - cardHeight - 10;
    }

    setHoverPosition({ x, y });
    setHoveredExecution(execution);
  };

  const handleRowLeave = () => {
    setHoveredExecution(null);
  };

  const handleViewExecution = (executionId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    window.open(`/workflows/executions/${executionId}`, '_blank');
  };

  const sortedExecutions = [...executions].sort((a, b) => {
    let comparison = 0;

    switch (sortField) {
      case 'status':
        comparison = a.status.localeCompare(b.status);
        break;
      case 'created_at':
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        break;
      case 'duration': {
        const phases = (a.phases || []) as any[];
        const phasesB = (b.phases || []) as any[];
        const durationA = a.end_time
          ? new Date(a.end_time).getTime() - new Date(a.created_at).getTime()
          : Date.now() - new Date(a.created_at).getTime();
        const durationB = b.end_time
          ? new Date(b.end_time).getTime() - new Date(b.created_at).getTime()
          : Date.now() - new Date(b.created_at).getTime();
        comparison = durationA - durationB;
        break;
      }
      case 'progress': {
        const phasesA = (a.phases || []) as any[];
        const phasesB = (b.phases || []) as any[];
        const completedA = phasesA.filter((p: any) => p.status === 'completed').length;
        const completedB = phasesB.filter((p: any) => p.status === 'completed').length;
        const progressA = phasesA.length ? completedA / phasesA.length : 0;
        const progressB = phasesB.length ? completedB / phasesB.length : 0;
        comparison = progressA - progressB;
        break;
      }
    }

    return sortDirection === 'asc' ? comparison : -comparison;
  });

  const SortIcon = ({ field }: { field: string }) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ?
      <FiChevronUp className="w-3 h-3 ml-1" /> :
      <FiChevronDown className="w-3 h-3 ml-1" />;
  };

  if (executions.length === 0) {
    return (
      <div className="text-center py-8">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
          darkMode ? 'bg-gray-700' : 'bg-gray-100'
        }`}>
          <FiActivity className={`w-8 h-8 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
        </div>
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          {metrics !== undefined ? 'No executions yet' : 'Loading executions...'}
        </p>
        <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          Run this workflow to see execution history
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-2 relative">
      <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
        {/* Table Header */}
        <div className={`grid grid-cols-5 gap-2 px-4 py-3 text-xs font-semibold uppercase tracking-wider ${
          darkMode
            ? 'text-gray-300 bg-gradient-to-r from-gray-800 to-gray-750'
            : 'text-gray-700 bg-gradient-to-r from-gray-50 to-gray-100'
        } border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <button
            className="flex items-center justify-start hover:text-blue-500 transition-colors group"
            onClick={() => handleSort('status')}
          >
            <span className="group-hover:scale-105 transition-transform">Status</span>
            <SortIcon field="status" />
          </button>
          <button
            className="flex items-center justify-start hover:text-blue-500 transition-colors group"
            onClick={() => handleSort('created_at')}
          >
            <span className="group-hover:scale-105 transition-transform">Date</span>
            <SortIcon field="created_at" />
          </button>
          <button
            className="flex items-center justify-start hover:text-blue-500 transition-colors group"
            onClick={() => handleSort('duration')}
          >
            <span className="group-hover:scale-105 transition-transform">Duration</span>
            <SortIcon field="duration" />
          </button>
          <button
            className="flex items-center justify-start hover:text-blue-500 transition-colors group"
            onClick={() => handleSort('progress')}
          >
            <span className="group-hover:scale-105 transition-transform">Progress</span>
            <SortIcon field="progress" />
          </button>
          <div className="text-center">Actions</div>
        </div>

        {/* Table Body */}
        <div className="max-h-48 overflow-y-auto">
          {sortedExecutions.slice(0, 6).map((execution, index) => {
            const durationMs = execution.end_time
              ? new Date(execution.end_time).getTime() - new Date(execution.created_at).getTime()
              : Date.now() - new Date(execution.created_at).getTime();

            const phases = (execution.phases || []) as any[];
            const phasesCompleted = phases.filter((p: any) => p.status === 'completed').length;
            const isEven = index % 2 === 0;

            return (
              <div
                key={execution.id}
                className={`grid grid-cols-5 gap-2 px-4 py-3 text-xs transition-all duration-200 border-b border-opacity-50 group ${
                  isEven
                    ? darkMode ? 'bg-gray-800/40' : 'bg-gray-50/60'
                    : darkMode ? 'bg-gray-900/40' : 'bg-white'
                } hover:${darkMode ? 'bg-gray-700/60' : 'bg-blue-50/80'} hover:shadow-sm ${
                  darkMode ? 'border-gray-700' : 'border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-1">
                  <div className={`
                    w-2.5 h-2.5 rounded-full shadow-sm transition-all duration-200 group-hover:scale-110
                    ${execution.status === 'completed' ? 'bg-gradient-to-r from-green-400 to-green-500 shadow-green-500/30' :
                      execution.status === 'failed' ? 'bg-gradient-to-r from-red-400 to-red-500 shadow-red-500/30' :
                      execution.status === 'active' ? 'bg-gradient-to-r from-blue-400 to-blue-500 shadow-blue-500/30 animate-pulse' :
                      'bg-gradient-to-r from-yellow-400 to-yellow-500 shadow-yellow-500/30'}
                  `} />
                  <span className="capitalize truncate font-normal text-xs">{execution.status}</span>
                  <button
                    className={`ml-1 p-1 rounded-full hover:${darkMode ? 'bg-gray-600' : 'bg-gray-200'} transition-all duration-200 opacity-0 group-hover:opacity-100 hover:scale-110`}
                    onMouseEnter={(e) => {
                      e.stopPropagation();
                      handleRowHover(execution, e);
                    }}
                    onMouseLeave={(e) => {
                      e.stopPropagation();
                      handleRowLeave();
                    }}
                    onClick={(e) => e.stopPropagation()}
                    title="View execution overview"
                  >
                    <FiInfo className="w-3 h-3" />
                  </button>
                </div>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-medium`}>
                  {new Date(execution.created_at).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric'
                  })}
                </div>
                <div className={`${darkMode ? 'text-gray-400' : 'text-gray-500'} font-mono`}>
                  {formatDuration(durationMs)}
                </div>
                <div className="flex items-center space-x-2">
                  <div className={`w-10 h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                    <div
                      className="h-2 rounded-full bg-gradient-to-r from-blue-400 to-blue-500 transition-all duration-300"
                      style={{
                        width: `${phases.length ? Math.round((phasesCompleted / phases.length) * 100) : 0}%`
                      }}
                    />
                  </div>
                  <span className={`text-xs font-mono ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {phasesCompleted}/{phases.length}
                  </span>
                </div>
                <div className="flex justify-center">
                  <button
                    onClick={(e) => handleViewExecution(execution.id, e)}
                    className={`p-1.5 rounded-lg hover:${darkMode ? 'bg-gray-600' : 'bg-gray-200'} transition-all duration-200 hover:scale-110 opacity-0 group-hover:opacity-100`}
                    title="View execution details"
                  >
                    <FiActivity className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {executions.length > 6 && (
          <div className={`text-center text-xs py-3 border-t ${
            darkMode
              ? 'text-gray-400 border-gray-700 bg-gray-800/20'
              : 'text-gray-500 border-gray-200 bg-gray-50/50'
          }`}>
            <span className="inline-flex items-center space-x-1">
              <FiHash className="w-3 h-3" />
              <span>+{executions.length - 6} more executions</span>
            </span>
          </div>
        )}
      </div>

      {/* Hover Card Portal */}
      {hoveredExecution && createPortal(
        <div
          className="fixed z-50 pointer-events-none"
          style={{
            left: `${hoverPosition.x}px`,
            top: `${hoverPosition.y}px`
          }}
        >
          <WorkflowExecutionCard
            execution={hoveredExecution}
            compact={true}
            className="w-80 animate-in fade-in-0 zoom-in-95 duration-200 shadow-2xl"
          />
        </div>,
        document.body
      )}
    </div>
  );
};

interface WorkflowCardProps {
  workflow: {
    id: string;
    name: string;
    description: string;
    phases: any[];
    connections: any[];
    nodes: any[];
    created_at: string;
    updated_at: string;
  };
  executions?: WorkflowExecution[];
  metrics?: {
    totalExecutions: number;
    successRate: number;
    avgDuration: number;
    lastExecuted?: string;
    totalArtifacts: number;
    activePersonas: number;
  };
  onClick?: (workflowId: string) => void;
  onHover?: () => void;
  onDelete?: () => void;
}


export const WorkflowCard: React.FC<WorkflowCardProps> = ({
  workflow,
  executions = [],
  metrics,
  onClick,
  onHover,
  onDelete
}) => {
  const { darkMode } = useTheme();
  const [activeTab, setActiveTab] = useState<'overview' | 'executions' | 'metrics'>('overview');

  // Trigger data loading when switching to executions or metrics tab
  React.useEffect(() => {
    if ((activeTab === 'executions' || activeTab === 'metrics') && executions.length === 0 && !metrics && onHover) {
      onHover();
    }
  }, [activeTab, executions.length, metrics, onHover, workflow.name]);

  // Calculate workflow complexity score
  const complexityScore = Math.min(
    Math.round(
      ((workflow.phases?.length || 0) * 2) +
      ((workflow.connections?.length || 0) * 1.5) +
      ((workflow.nodes?.length || 0) * 1)
    ),
    100
  );

  // Get status distribution
  const statusCounts = executions.reduce((acc, exec) => {
    acc[exec.status] = (acc[exec.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Get recent execution trend (last 5)
  const recentExecutions = executions
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  // Calculate success rate
  const successRate = executions.length > 0
    ? Math.round((statusCounts.completed || 0) / executions.length * 100)
    : 0;

  // Get workflow health score
  const healthScore = Math.round(
    (successRate * 0.4) +
    (complexityScore > 50 ? (100 - complexityScore) * 0.3 : complexityScore * 0.3) +
    (executions.length > 0 ? 30 : 0)
  );

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'from-blue-500 to-cyan-500';
      case 'completed': return 'from-green-500 to-emerald-500';
      case 'failed': return 'from-red-500 to-pink-500';
      case 'paused': return 'from-yellow-500 to-orange-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  // Get health color and icon
  const getHealthColor = (score: number) => {
    if (score >= 80) return {
      bg: darkMode ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-green-400 to-emerald-400',
      text: 'text-white',
      icon: FiAward
    };
    if (score >= 60) return {
      bg: darkMode ? 'bg-gradient-to-r from-yellow-500 to-orange-500' : 'bg-gradient-to-r from-yellow-400 to-orange-400',
      text: 'text-white',
      icon: FiTrendingUp
    };
    return {
      bg: darkMode ? 'bg-gradient-to-r from-red-500 to-pink-500' : 'bg-gradient-to-r from-red-400 to-pink-400',
      text: 'text-white',
      icon: FiAlertTriangle
    };
  };

  const healthInfo = getHealthColor(healthScore);
  const HealthIcon = healthInfo.icon;

  // Get complexity description
  const getComplexityDescription = (score: number) => {
    if (score <= 20) return { text: 'Simple', color: 'text-green-500', icon: FiCheck };
    if (score <= 40) return { text: 'Moderate', color: 'text-blue-500', icon: FiLayers };
    if (score <= 60) return { text: 'Complex', color: 'text-yellow-500', icon: FiGitBranch };
    if (score <= 80) return { text: 'Advanced', color: 'text-orange-500', icon: FiSettings };
    return { text: 'Expert', color: 'text-red-500', icon: FiAlertTriangle };
  };

  const complexityInfo = getComplexityDescription(complexityScore);
  const ComplexityIcon = complexityInfo.icon;

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-3">
            {/* Recent Activity */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-semibold flex items-center">
                  <FiActivity className="mr-2 text-blue-500" />
                  Recent Activity
                </span>
                <span className="text-xs text-gray-500 bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded-full">
                  {recentExecutions.filter(e => e.status === 'completed').length}/{recentExecutions.length} successful
                </span>
              </div>
              <div className="flex space-x-2">
                {recentExecutions.map((execution) => {
                  return (
                    <div
                      key={execution.id}
                      className={`
                        w-10 h-10 rounded-xl flex items-center justify-center shadow-lg transition-all duration-200 hover:scale-110 cursor-pointer
                        ${execution.status === 'completed' ? 'bg-gradient-to-br from-green-400 to-green-500 shadow-green-500/30' :
                          execution.status === 'failed' ? 'bg-gradient-to-br from-red-400 to-red-500 shadow-red-500/30' :
                          execution.status === 'active' ? 'bg-gradient-to-br from-blue-400 to-blue-500 shadow-blue-500/30 animate-pulse' :
                          'bg-gradient-to-br from-yellow-400 to-yellow-500 shadow-yellow-500/30'}
                      `}
                      title={`${execution.status.charAt(0).toUpperCase() + execution.status.slice(1)} - ${new Date(execution.created_at).toLocaleDateString()}`}
                    >
                      {execution.status === 'active' && <FiPlay className="text-white text-sm" />}
                      {execution.status === 'completed' && <FiCheck className="text-white text-sm" />}
                      {execution.status === 'failed' && <FiX className="text-white text-sm" />}
                      {execution.status === 'paused' && <FiPause className="text-white text-sm" />}
                    </div>
                  );
                })}
                {Array.from({ length: Math.max(0, 5 - recentExecutions.length) }).map((_, index) => (
                  <div key={`empty-${index}`} className="w-10 h-10 rounded-xl bg-gray-200 dark:bg-gray-700 opacity-30 border-2 border-dashed border-gray-300 dark:border-gray-600" />
                ))}
              </div>
            </div>

            {/* Architecture Summary */}
            <div className={`p-4 rounded-xl border-2 border-dashed transition-all duration-200 hover:border-solid ${
              darkMode ? 'bg-gray-800/30 border-gray-700 hover:bg-gray-800/50' : 'bg-gray-50/50 border-gray-200 hover:bg-gray-50/80'
            }`}>
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <div className={`p-2 rounded-lg ${complexityInfo.color.replace('text-', 'bg-').replace('-500', '-100')} dark:${complexityInfo.color.replace('text-', 'bg-').replace('-500', '-900')}`}>
                    <ComplexityIcon className={`w-4 h-4 ${complexityInfo.color}`} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-1">Architecture</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      {complexityInfo.text} workflow with {workflow.phases?.length || 0} phases
                      {workflow.connections?.filter(conn => conn.dependencyType === 'persona_assignment').length > 0 &&
                        ` and ${workflow.connections.filter(conn => conn.dependencyType === 'persona_assignment').length} personas`}
                    </div>
                  </div>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs font-bold ${complexityInfo.color} bg-opacity-10`}>
                  {complexityScore}
                </div>
              </div>
            </div>

            {/* Performance Insight */}
            {metrics?.avgDuration ? (
              <div className={`p-4 rounded-xl border transition-all duration-200 ${
                darkMode ? 'bg-gradient-to-r from-purple-900/20 to-blue-900/20 border-purple-700/30' : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-200/50'
              }`}>
                <div className="flex items-start space-x-3">
                  <div className="p-2 rounded-lg bg-gradient-to-r from-purple-500 to-blue-500 text-white">
                    <FiZap className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-sm font-semibold mb-1">Performance</div>
                    <div className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
                      Averaging <span className="font-mono font-semibold">{Math.round(metrics.avgDuration / 60000)}min</span> runtime
                      {metrics.totalArtifacts ? ` • ${Math.round(metrics.totalArtifacts / executions.length)} artifacts per run` : ''}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={`p-4 rounded-xl border-2 border-dashed ${
                darkMode ? 'border-gray-700 bg-gray-800/20' : 'border-gray-300 bg-gray-100/30'
              }`}>
                <div className="flex items-center justify-center space-x-2 text-gray-500">
                  <FiBarChart className="w-4 h-4" />
                  <span className="text-sm">No performance data yet</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'structure':
        return (
          <div className="h-full overflow-y-auto space-y-3">
            {/* Phases Section */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold flex items-center">
                  <FiLayers className="mr-1.5 text-purple-500 w-3 h-3" />
                  Phases ({workflow.phases?.length || 0})
                </span>
              </div>
              <div className="space-y-1">
                {workflow.phases?.length > 0 ? (
                  workflow.phases.map((phase, index) => (
                    <div
                      key={phase.id}
                      className={`px-2 py-1 rounded-md border transition-all duration-200 ${
                        darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                          darkMode ? 'bg-purple-600 text-white' : 'bg-purple-100 text-purple-600'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-xs font-medium truncate">{phase.label} • {phase.type || 'Standard'}</div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`px-2 py-1 rounded-md border-2 border-dashed ${
                    darkMode ? 'border-gray-700 bg-gray-800/20' : 'border-gray-300 bg-gray-100/30'
                  }`}>
                    <div className="flex items-center justify-center space-x-1 text-gray-500">
                      <FiLayers className="w-3 h-3" />
                      <span className="text-xs">No phases</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Personas Section */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold flex items-center">
                  <FiUsers className="mr-1.5 text-blue-500 w-3 h-3" />
                  Personas ({workflow.connections?.filter(conn => conn.dependencyType === 'persona_assignment').length || 0})
                </span>
              </div>
              <div className="space-y-1">
                {(() => {
                  const personaConnections = workflow.connections?.filter(conn =>
                    conn.dependencyType === 'persona_assignment' && conn.targetHandle === 'personas') || [];

                  const reviewerConnections = workflow.connections?.filter(conn =>
                    conn.dependencyType === 'persona_assignment' && conn.targetHandle === 'reviewer') || [];

                  const allPersonaConnections = [...personaConnections, ...reviewerConnections];

                  if (allPersonaConnections.length > 0) {
                    return allPersonaConnections.map((connection) => {
                      const personaNode = workflow.nodes?.find(node => node.id === connection.source);
                      const personaId = personaNode?.data?.personaId || 'Unknown';
                      const personaName = personaNode?.data?.personaName || personaId;
                      const targetPhase = workflow.phases?.find(phase => phase.id === connection.target);
                      const isReviewer = connection.targetHandle === 'reviewer';

                      return (
                        <div
                          key={`${connection.source}-${connection.target}`}
                          className={`px-2 py-1 rounded-md border transition-all duration-200 ${
                            darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-xs font-bold ${
                              isReviewer
                                ? darkMode ? 'bg-orange-600 text-white' : 'bg-orange-100 text-orange-600'
                                : darkMode ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-600'
                            }`}>
                              {isReviewer ? '👁️' : personaName.charAt(0).toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium truncate">
                                {personaName} → {targetPhase?.label || connection.target}
                                {isReviewer && <span className="ml-1 text-orange-500">• Reviewer</span>}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  } else {
                    return (
                      <div className={`px-2 py-1 rounded-md border-2 border-dashed ${
                        darkMode ? 'border-gray-700 bg-gray-800/20' : 'border-gray-300 bg-gray-100/30'
                      }`}>
                        <div className="flex items-center justify-center space-x-1 text-gray-500">
                          <FiUsers className="w-3 h-3" />
                          <span className="text-xs">No personas</span>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>

            {/* Tools Section */}
            <div className="flex-shrink-0">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold flex items-center">
                  <FiTool className="mr-1.5 text-green-500 w-3 h-3" />
                  Tools ({workflow.connections?.filter(conn => conn.dependencyType === 'tool_assignment').length || 0})
                </span>
              </div>
              <div className="space-y-1">
                {(() => {
                  const toolConnections = workflow.connections?.filter(conn =>
                    conn.dependencyType === 'tool_assignment') || [];

                  if (toolConnections.length > 0) {
                    return toolConnections.map((connection) => {
                      const toolNode = workflow.nodes?.find(node => node.id === connection.source);
                      const toolId = toolNode?.data?.toolId || 'Unknown';
                      const toolName = toolNode?.data?.toolName || toolId;
                      const targetPhase = workflow.phases?.find(phase => phase.id === connection.target);

                      return (
                        <div
                          key={`${connection.source}-${connection.target}`}
                          className={`px-2 py-1 rounded-md border transition-all duration-200 ${
                            darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            <FiTool className="w-3 h-3 text-green-500 flex-shrink-0" />
                            <div className="min-w-0 flex-1">
                              <div className="text-xs font-medium truncate">{toolName} → {targetPhase?.label || connection.target}</div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                  } else {
                    return (
                      <div className={`px-2 py-1 rounded-md border-2 border-dashed ${
                        darkMode ? 'border-gray-700 bg-gray-800/20' : 'border-gray-300 bg-gray-100/30'
                      }`}>
                        <div className="flex items-center justify-center space-x-1 text-gray-500">
                          <FiTool className="w-3 h-3" />
                          <span className="text-xs">No tools</span>
                        </div>
                      </div>
                    );
                  }
                })()}
              </div>
            </div>
          </div>
        );

      case 'executions':
        return (
          <ExecutionsTable
            executions={executions}
            metrics={metrics}
            darkMode={darkMode}
          />
        );

      case 'metrics':
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              {/* Architecture */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                darkMode ? 'bg-indigo-900/20 border-indigo-700/30' : 'bg-indigo-50 border-indigo-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <FiLayers className="w-4 h-4 text-indigo-500" />
                  <span className="text-lg font-bold">{workflow.phases?.length || 0}</span>
                </div>
                <div className="text-xs font-medium">Phases</div>
                <div className="text-xs text-gray-500 mt-1">
                  {workflow.connections?.length || 0} connections
                </div>
              </div>

              {/* Reliability */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                successRate >= 80
                  ? darkMode ? 'bg-green-900/20 border-green-700/30' : 'bg-green-50 border-green-200'
                  : successRate >= 60
                  ? darkMode ? 'bg-yellow-900/20 border-yellow-700/30' : 'bg-yellow-50 border-yellow-200'
                  : darkMode ? 'bg-red-900/20 border-red-700/30' : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <FiTarget className={`w-4 h-4 ${
                    successRate >= 80 ? 'text-green-500' : successRate >= 60 ? 'text-yellow-500' : 'text-red-500'
                  }`} />
                  <span className="text-lg font-bold">{successRate}%</span>
                </div>
                <div className="text-xs font-medium">Success Rate</div>
                <div className="text-xs text-gray-500 mt-1">
                  {statusCounts.completed || 0} ✅ {statusCounts.failed || 0} ❌
                </div>
              </div>

              {/* Performance */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                darkMode ? 'bg-blue-900/20 border-blue-700/30' : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <FiZap className="w-4 h-4 text-blue-500" />
                  <span className="text-lg font-bold font-mono">
                    {metrics?.avgDuration ? `${Math.round(metrics.avgDuration / 60000)}m` : 'N/A'}
                  </span>
                </div>
                <div className="text-xs font-medium">Avg Runtime</div>
                <div className="text-xs text-gray-500 mt-1">
                  {executions.length} total runs
                </div>
              </div>

              {/* Output */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                darkMode ? 'bg-purple-900/20 border-purple-700/30' : 'bg-purple-50 border-purple-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <FiDatabase className="w-4 h-4 text-purple-500" />
                  <span className="text-lg font-bold">{metrics?.totalArtifacts || 0}</span>
                </div>
                <div className="text-xs font-medium">Artifacts</div>
                <div className="text-xs text-gray-500 mt-1">
                  Total generated
                </div>
              </div>

              {/* Team */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                darkMode ? 'bg-cyan-900/20 border-cyan-700/30' : 'bg-cyan-50 border-cyan-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <FiUsers className="w-4 h-4 text-cyan-500" />
                  <span className="text-lg font-bold">{metrics?.activePersonas || 0}</span>
                </div>
                <div className="text-xs font-medium">Personas</div>
                <div className="text-xs text-gray-500 mt-1">
                  Active agents
                </div>
              </div>

              {/* Complexity */}
              <div className={`p-3 rounded-xl border transition-all duration-200 hover:shadow-md ${
                darkMode ? 'bg-orange-900/20 border-orange-700/30' : 'bg-orange-50 border-orange-200'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <ComplexityIcon className={`w-4 h-4 ${complexityInfo.color}`} />
                  <span className="text-lg font-bold">{complexityScore}</span>
                </div>
                <div className="text-xs font-medium">Complexity</div>
                <div className="text-xs text-gray-500 mt-1">
                  {complexityInfo.text} level
                </div>
              </div>
            </div>

            {/* Hot Streak Badge */}
            {recentExecutions.length >= 3 && recentExecutions.slice(0, 3).every(e => e.status === 'completed') && (
              <div className={`p-3 rounded-xl border-2 transition-all duration-200 ${
                darkMode
                  ? 'bg-gradient-to-r from-orange-900/30 to-red-900/30 border-orange-500/50'
                  : 'bg-gradient-to-r from-orange-100 to-red-100 border-orange-300'
              }`}>
                <div className="flex items-center space-x-2">
                  <div className="text-2xl">🔥</div>
                  <div>
                    <div className="text-sm font-bold text-orange-600 dark:text-orange-400">
                      Hot Streak!
                    </div>
                    <div className="text-xs text-orange-700 dark:text-orange-300">
                      3+ consecutive successful executions
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Last Executed */}
            {metrics?.lastExecuted && (
              <div className={`p-3 rounded-xl border ${
                darkMode ? 'bg-gray-800/30 border-gray-700' : 'bg-gray-50 border-gray-200'
              }`}>
                <div className="flex items-center space-x-2 text-xs">
                  <FiCalendar className="w-3 h-3 text-gray-500" />
                  <span className="text-gray-600 dark:text-gray-400">
                    Last executed: {new Date(metrics.lastExecuted).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const cardContent = (
    <div
      className={`
        relative overflow-hidden rounded-2xl h-[520px] group
        transition-all duration-300 ease-out
        hover:-translate-y-2 hover:scale-[1.02]
        ${darkMode
          ? 'bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border-2 border-gray-700 hover:border-gray-600'
          : 'bg-gradient-to-br from-white via-gray-50 to-white border-2 border-gray-200 hover:border-gray-300'
        }
        shadow-xl hover:shadow-2xl
        before:absolute before:inset-0 before:bg-gradient-to-br before:from-blue-500/5 before:to-purple-500/5 before:opacity-0 hover:before:opacity-100 before:transition-opacity before:duration-300
      `}
      onMouseEnter={() => {
        if (onHover && executions.length === 0 && !metrics) {
          onHover();
        }
      }}
    >
      {/* Delete button — top-right, hover-revealed, stops click propagation */}
      {onDelete && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          title="Delete workflow"
          aria-label={`Delete workflow ${workflow.name || 'unnamed'}`}
          className={`absolute top-3 right-3 z-20 p-1.5 rounded-md opacity-0 group-hover:opacity-100 transition-opacity ${
            darkMode
              ? 'bg-gray-800/80 hover:bg-red-900/70 text-red-400 border border-gray-700'
              : 'bg-white/90 hover:bg-red-50 text-red-600 border border-gray-200'
          }`}
        >
          <FiTrash2 className="w-4 h-4" />
        </button>
      )}
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full -translate-y-16 translate-x-16 group-hover:scale-150 transition-transform duration-500" />
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-500/10 to-blue-500/10 rounded-full translate-y-12 -translate-x-12 group-hover:scale-125 transition-transform duration-500" />

      {/* Header */}
      <div className="p-4 relative z-10">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 pr-4">
            <h3 className="text-lg font-bold truncate text-gray-900 dark:text-gray-100 mb-1 leading-tight group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
              {workflow.name}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-300 line-clamp-2 leading-relaxed">
              {workflow.description || 'No description available'}
            </p>
          </div>

          <div className="flex flex-col items-end space-y-2 flex-shrink-0">
            <div className={`
              px-2 py-1 rounded-lg text-xs font-bold shadow-md transition-all duration-200 hover:scale-110 flex items-center space-x-1
              ${healthInfo.bg} ${healthInfo.text}
            `}>
              <HealthIcon className="w-3 h-3" />
              <span>{healthScore}%</span>
            </div>

            <Link
              to={`/workflows/${workflow.id}`}
              className="p-2 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white transition-all duration-200 shadow-md hover:shadow-lg hover:scale-110 group/button"
              onClick={(e) => e.stopPropagation()}
              title="View Workflow"
            >
              <FiPlay className="w-4 h-4 group-hover/button:scale-110 transition-transform duration-200" />
            </Link>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={`border-b px-4 relative z-10 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex space-x-0">
          {[
            { id: 'overview', label: 'Overview', icon: FiActivity },
            { id: 'structure', label: 'Structure', icon: FiLayers },
            { id: 'executions', label: 'Executions', icon: FiPlay },
            { id: 'metrics', label: 'Metrics', icon: FiBarChart }
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setActiveTab(tab.id as 'overview' | 'executions' | 'metrics');
              }}
              className={`
                flex items-center space-x-1.5 px-3 py-3 text-xs font-semibold transition-all duration-200
                border-b-2 cursor-pointer relative group/tab
                ${activeTab === tab.id
                  ? darkMode
                    ? 'text-blue-400 border-blue-400 bg-blue-900/20'
                    : 'text-blue-600 border-blue-600 bg-blue-50/70'
                  : darkMode
                    ? 'text-gray-400 border-transparent hover:text-gray-300 hover:border-gray-600 hover:bg-gray-800/30'
                    : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300 hover:bg-gray-50/50'
                }
              `}
              style={{ pointerEvents: 'auto' }}
            >
              <tab.icon className="w-3 h-3 group-hover/tab:scale-110 transition-transform duration-200" />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-4 h-64 overflow-hidden relative z-10">
        {renderTabContent()}
      </div>

      {/* Footer */}
      <div className={`
        absolute bottom-0 left-0 right-0 px-6 py-4 text-sm
        flex items-center justify-between
        ${darkMode ? 'text-gray-300 bg-gray-900/90 backdrop-blur-md' : 'text-gray-500 bg-white/90 backdrop-blur-md'}
        border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}
        relative z-10
      `}>
        <span className="text-xs flex items-center space-x-2">
          <FiCalendar className="w-3 h-3" />
          <span>
            Updated {new Date(workflow.updated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric'
            })}
          </span>
        </span>

        <div className="flex items-center space-x-3">
          <Link
            to={`/workflows/${workflow.id}`}
            className={`
              px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200
              flex items-center space-x-2 shadow-md hover:shadow-lg hover:scale-105 group/link
              bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <FiPlay className="w-3 h-3 group-hover/link:scale-110 transition-transform duration-200" />
            <span>View</span>
          </Link>

          <Link
            to={`/workflows/${workflow.id}/edit`}
            className={`
              px-4 py-2 rounded-xl text-xs font-semibold transition-all duration-200
              flex items-center space-x-2 shadow-md hover:shadow-lg hover:scale-105 group/link
              bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white
            `}
            onClick={(e) => e.stopPropagation()}
          >
            <FiEdit className="w-3 h-3 group-hover/link:scale-110 transition-transform duration-200" />
            <span>Edit</span>
          </Link>
        </div>
      </div>

    </div>
  );

  return onClick ? (
    <div className="cursor-pointer">
      {cardContent}
    </div>
  ) : (
    cardContent
  );
};
